import React, { useEffect, useState } from "react";
import { LoadingContainer } from "go-core/components/Loading";
import { ReportCombo } from "../../../ReportCombo";
import { ReportComboContext } from "../../services/context";
import { KpisConfigReportComboApi, VariableConfigReportComboApi } from "../../services/types";
import { ReportComboUtils } from "../../services/utils";
import { BaseVariableReportCombo } from "./Variable/BaseVariableReportCombo";
import { MonthReportCombo } from "./Variable/MonthReportCombo";
import { QuarterReportCombo } from "./Variable/QuarterReportCombo";
import { WeekReportCombo } from "./Variable/WeekReportCombo";
import { TemplateReportCombo } from "./types";

const parseConfigValue = (value: string, replaces: Record<string, string>) => {
	let newValue = value;
	Object.entries(replaces).forEach(([key, replace]) => {
		if (replace) {
			newValue = newValue.replace(`{${key}}`, replace);
		}
	});
	return newValue;
};

const parseConfig = (configs: KpisConfigReportComboApi, valueVariables: Record<string, string>) => {
	const newConfigs: KpisConfigReportComboApi = JSON.parse(JSON.stringify(configs));

	newConfigs.kpis.forEach((config) => {
		config.data.forEach((configData) => {
			configData.filter = parseConfigValue(configData.filter, valueVariables);
			configData.columns.forEach((configDataColumn) => {
				configDataColumn.filter = parseConfigValue(configDataColumn.filter, valueVariables);
				if (configDataColumn.compare?.filter) {
					configDataColumn.compare.filter = parseConfigValue(
						configDataColumn.compare?.filter,
						valueVariables
					);
				}
			});
			if (configData.groups) {
				configData.groups = configData.groups.map((configDataGroup) => {
					return parseConfigValue(configDataGroup, valueVariables);
				});
			}
		});
		config.rows?.forEach((configRow) => {
			if (configRow.group) configRow.group = parseConfigValue(configRow.group, valueVariables);
		});
		if (config.table) {
			config.table.forEach((configTable) => {
				configTable.rows.forEach((configRow) => {
					if (configRow.group) configRow.group = parseConfigValue(configRow.group, valueVariables);
				});
				configTable.rows.forEach((configRow) => {
					if (configRow.group) configRow.group = parseConfigValue(configRow.group, valueVariables);
				});
				configTable.columns.forEach((configColumn) => {
					if (configColumn.values_dynamic)
						configColumn.values_dynamic = parseConfigValue(configColumn.values_dynamic, valueVariables);
				});
			});
		}
	});

	return newConfigs;
};

const parseTemplates = (template: TemplateReportCombo): KpisConfigReportComboApi => {
	const kpis = template.templates.flatMap((x) => x.data);
	return {
		kpis,
	};
};

interface Props {
	template: TemplateReportCombo;
	onChangeFilter?: (filter: Record<string, string>) => void;
}

const parseFilterBaseOnVariableNew = (newFilter: Record<string, string>, variables: VariableConfigReportComboApi[]) => {
	Object.entries(newFilter).forEach(([key, value]) => {
		const variable = variables.filter((x) => x.id === key)[0];
		if (!variable) return;
		if (!variable.calculate) return;
		const newValue = ReportComboUtils.parseCustomFilter(value, variable.calculate.method);
		if (newValue) {
			newFilter[key] = newValue;
		}
	});
	return newFilter;
};
export const TemplateReportComboComponent = (props: Props) => {
	const config = parseTemplates(props.template);
	const [filter, setFilter] = useState<Record<string, string> | undefined>(props.template.filter);
	const [loading, setLoading] = useState(true);
	const [show, setShow] = useState(true);
	const variables = Array.from(
		new Map((config.kpis?.flatMap((x) => x?.variables ?? []) ?? []).map((v) => [v.id, v])).values()
	);
	let tmpFilter = parseFilterBaseOnVariableNew({ ...filter }, variables);
	const configParsed = parseConfig(config, tmpFilter ? tmpFilter : {});
	const reportComboService = React.useContext(ReportComboContext);

	useEffect(() => {
		setLoading(false);
	}, []);

	if (loading) {
		return <LoadingContainer />;
	}

	const notExistFilterIds = variables
		.filter((variable) => {
			if (variable.type === "DYNAMIC") return false;
			if (!tmpFilter[variable.id]) return true;
			return false;
		})
		.map((variable) => variable.id);

	const updateFilters = (value: Record<string, string>) => {
		const newFilter = filter ? { ...filter, ...value } : { ...value };
		const keys = Object.keys(newFilter);
		keys.forEach((key) => {
			if (!variables.some((x) => x.id === key)) {
				delete newFilter[key];
			}
		});

		setFilter(newFilter);
		if (props.onChangeFilter) props.onChangeFilter(newFilter);
	};

	const renderVariableFilters = (variable: VariableConfigReportComboApi) => {
		if (variable.type && variable.type === "DYNAMIC") return;
		const filterType = variable.type;
		const variableId = variable.id;
		// const filterType = variableId.split("__")[1];

		const filterVariableInput = reportComboService.getFilterVariableComponent(
			variableId,
			(value) => updateFilters(value),
			filter
		);
		if (filterVariableInput) return filterVariableInput;
		switch (filterType) {
			case "QUARTER": {
				return (
					<>
						<QuarterReportCombo
							name={variableId}
							filter={filter}
							onChange={(value) => updateFilters(value)}
						/>
						<hr />
					</>
				);
			}
			case "WEEK": {
				return (
					<>
						<WeekReportCombo name={variableId} filter={filter} onChange={(value) => updateFilters(value)} />
						<hr />
					</>
				);
			}
			case "MONTH": {
				return (
					<>
						<MonthReportCombo
							name={variableId}
							filter={filter}
							onChange={(value) => updateFilters(value)}
						/>
						<hr />
					</>
				);
			}
			default: {
				return (
					<>
						<BaseVariableReportCombo filter={filter} name={variableId} onChange={updateFilters} />
						<hr />
					</>
				);
			}
		}
	};

	return (
		<>
			{variables.map((variable) => {
				return <React.Fragment key={variable.id}>{renderVariableFilters(variable)}</React.Fragment>;
			})}

			{notExistFilterIds.length > 0 ? (
				<>
					Wybierz wymagane filtry:
					{notExistFilterIds.join(", ")}
				</>
			) : (
				<>
					{show && (
						<>
							{configParsed.kpis.map((kpi) => (
								<div key={kpi.id}>
									{/*{configParsed.kpis.length > 1 && (*/}
									<div>
										<h3 className={"mb-2 mt-4"}>{kpi.name}</h3>
										{kpi.description && <div>{kpi.description}</div>}
										<hr />
									</div>
									{/*)}*/}
									<ReportCombo config={kpi} key={kpi.id} variableValues={tmpFilter} />
								</div>
							))}
						</>
					)}
				</>
			)}
		</>
	);
};
